import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * GetSale → Horizon cart bridge (cart page only).
 *
 * GetSale completes its own cart mutations — offer adds and automatic removal
 * of invalid offer lines — without emitting Horizon's standard cart event, so
 * the cart table, summary, and header count stay stale until reload. This
 * module listens for the two GetSale completion signals, reads the resulting
 * cart once, and notifies Horizon through the official CartLinesUpdateEvent
 * contract:
 *
 * - `getsale:cart-changed` (window): a GetSale offer was added.
 * - `getsale:composer-cart-updated` (document): GetSale removed offer lines.
 *
 * The event promise resolves without `detail.sections` on purpose: the live
 * cart-items-component then re-renders its own section through
 * sectionRenderer, keeping morph ownership in the theme. The bridge performs
 * no cart mutations, never re-emits GetSale completion signals, and never
 * dispatches a promise that can reject — a failed cart read is logged and
 * skipped instead.
 *
 * Reverse leg: GetSale re-evaluates its offers only on its own cart event
 * names, never on StandardEvents.cartLinesUpdate, so native Horizon cart edits
 * (quantity changes, removes) left its rendered offers stale until reload.
 * After a settled external cart update, the change is re-announced to GetSale
 * with `cart:refresh` — the same public signal its own add flow dispatches —
 * so its renderer re-reads the cart. The bridge's own notifications are
 * skipped: GetSale already re-renders on the completion signals that caused
 * them, and nothing else in this theme listens to `cart:refresh`.
 *
 * Page lifecycle: pagehide aborts the in-flight read and invalidates the
 * burst synchronously, so a hidden or frozen page can never apply a late
 * response and an immediate persisted pageshow can start a fresh read before
 * the aborted read settles; a persisted pageshow (BFCache restore) re-syncs
 * once through the same read → notify path.
 */

const SOURCE = 'getsale-theme-bridge';
const GETSALE_ROOT_SELECTOR = '[data-getsale-composer]';
const GATE_RETRY_DELAY_MS = 100;
const GATE_RETRY_ATTEMPTS = 5;

const pageSurface = (root) => {
  const configured = root.dataset.surface;
  if (configured && configured !== 'AUTO') return configured;
  return { product: 'PRODUCT', cart: 'CART' }[root.dataset.pageType] || 'STOREFRONT';
};

/** A signal is this bridge's business only while an enabled GetSale CART root exists. */
function getsaleRootEligible() {
  for (const root of document.querySelectorAll(GETSALE_ROOT_SELECTOR)) {
    if (root.dataset.enabled === 'true' && pageSurface(root) === 'CART') return true;
  }
  return false;
}

/** The post-read apply target: Horizon's live cart surface. */
const cartSurfaceConnected = () => Boolean(document.querySelector('cart-items-component'));

/** @param {AbortSignal} signal */
async function readCart(signal) {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
    signal,
  });
  if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
  const cart = await response.json();
  if (typeof cart?.token !== 'string' || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) {
    throw new Error('Cart read returned an unexpected payload');
  }
  return cart;
}

const cartSignature = (cart) =>
  JSON.stringify([cart.token, cart.item_count, cart.total_price, cart.total_discount,
    cart.items.map((item) => [item.key, item.quantity, item.final_line_price])]);

function notify(action, cart) {
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      detail: { source: SOURCE },
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: SOURCE, itemCount: cart.item_count },
      }),
    })
  );
}

/**
 * Checks that the live cart surface is available before applying a read.
 * cart-items-component can be absent at signal time or transiently
 * disconnected while its section morphs; both get bounded retries here so the
 * change is not silently dropped. Only a surface that stays unavailable is
 * dropped, with a warning. An abort (pagehide) returns false without being a
 * gate failure.
 * @param {AbortController} controller
 */
async function gateAvailable(controller) {
  for (let attempt = 0; attempt < GATE_RETRY_ATTEMPTS; attempt += 1) {
    if (controller.signal.aborted) return false;
    if (cartSurfaceConnected()) return true;
    if (attempt === GATE_RETRY_ATTEMPTS - 1) break;
    await new Promise((resolve) => {
      const timer = setTimeout(resolve, GATE_RETRY_DELAY_MS);
      controller.signal.addEventListener('abort', () => {
        clearTimeout(timer);
        resolve();
      }, { once: true });
    });
  }
  return false;
}

let syncing = false;
let queuedAction = null;
/** @type {AbortController | null} */
let activeController = null;
/** Bumped on pagehide: forwarder callbacks captured before it are stale. */
let pageEpoch = 0;

async function runSync(action, controller) {
  try {
    let current = action;
    // Dedupe is scoped to this burst only: coalesced signals for one already
    // notified snapshot must not double-render, but a later burst must always
    // notify — native cart changes between bursts can return the cart to a
    // previously seen snapshot that is no longer reflected in the UI.
    let notifiedSignature = null;
    while (current) {
      try {
        const cart = await readCart(controller.signal);
        const signature = cartSignature(cart);
        if (signature !== notifiedSignature) {
          if (!(await gateAvailable(controller))) {
            // An aborted gate is the pagehide path, not a drop-worthy failure.
            if (controller.signal.aborted) return;
            console.warn(`[${SOURCE}] Cart sync dropped: cart surface inactive after retries`, {
              action: current,
              itemCount: cart.item_count,
            });
            return;
          }
          notifiedSignature = signature;
          notify(current, cart);
        }
      } catch (error) {
        // AbortError is the pagehide cancellation path, not a sync failure.
        if (error?.name !== 'AbortError') {
          console.warn(`[${SOURCE}] Cart sync failed:`, error);
        }
      }
      if (controller.signal.aborted) return;
      // Signals that arrived during the read are drained by one more read, so
      // a change during the GET is never lost and reads never run in parallel.
      current = queuedAction;
      queuedAction = null;
    }
  } finally {
    if (activeController === controller) {
      activeController = null;
      syncing = false;
    }
  }
}

function requestSync(action) {
  if (!getsaleRootEligible()) return;
  if (syncing) {
    queuedAction = action;
    return;
  }
  const controller = new AbortController();
  activeController = controller;
  syncing = true;
  void runSync(action, controller);
}

window.addEventListener('getsale:cart-changed', () => requestSync('add'));
document.addEventListener('getsale:composer-cart-updated', () => requestSync('remove'));

/**
 * Forwards a settled external cart update to GetSale as `cart:refresh` so its
 * offers re-evaluate after native Horizon edits. Fires only after the event
 * promise resolves without errors — a rejected or failed update means the cart
 * did not change, and the bridge's own events are excluded to keep the two
 * legs from feeding each other. Settlements crossing a pagehide are dropped:
 * they belong to a hidden, frozen, or restored page epoch, and GetSale
 * re-discovers on pageshow by itself.
 * @param {CartLinesUpdateEvent} event
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.detail?.source === SOURCE) return;
  const epoch = pageEpoch;
  event.promise
    ?.then((result) => {
      if (epoch !== pageEpoch) return;
      if (result?.detail?.didError) return;
      if (getsaleRootEligible()) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    })
    .catch((error) => {
      if (epoch !== pageEpoch) return;
      if (error?.name !== 'AbortError') {
        console.warn(`[${SOURCE}] Native cart update failed; GetSale offers left unchanged:`, error);
      }
    });
});

window.addEventListener('pagehide', () => {
  pageEpoch += 1;
  // Invalidate the active burst synchronously: a persisted pageshow right
  // after can start a fresh read before the aborted read settles. The stale
  // runSync finally is identity-checked, so it cannot clobber the new burst.
  queuedAction = null;
  const controller = activeController;
  activeController = null;
  syncing = false;
  controller?.abort();
});
window.addEventListener('pageshow', (event) => {
  if (event.persisted) requestSync('update');
});
